/**
 * Tracks API hooks (catalog list bundle — Wave 2.4).
 *
 * Wraps `apiClient` (which already handles auth, 401-refresh, error envelope)
 * with a typed TanStack Query so the catalog page only knows about high-level
 * loading/error/data state.
 *
 * The backend endpoint `GET /admin/tracks` lands in TASK-037c (separate wave).
 * Until then this hook just renders the page with empty/loading/error states.
 */

import { useQuery } from '@tanstack/react-query';
import type { LibraryType, TrackListItem, TrackStatus } from '@music-app/shared';
import { apiClient } from '../lib/api-client';
import { STALE_TIME } from '../lib/query-client';
import type { PaginationState, SortingState } from '../components/DataTable';
import type { TrackFilters } from '../hooks/useTrackFilters';

export interface TracksQueryParams {
  /**
   * Optional — omit to query across BOTH libraries (the moderation queue
   * drives its result set by `source_type`, not library). The catalog page
   * always passes a concrete library from its `LibraryTabs`.
   */
  library?: LibraryType;
  pagination: PaginationState;
  sorting: SortingState[];
  filters?: TrackFilters;
  /**
   * Track provenance facet (`content.tracks.source_type`), forwarded as the
   * `source_type_in` CSV alias. The moderation queue pins this to
   * `['selector_curated']`. See `trackFilterSchema.source_type` in shared.
   */
  source_type?: string[];
}

export interface TracksListResponse {
  items: TrackListItem[];
  total: number;
  cursor: string | null;
}

const DEFAULT_SORT_ID = 'created_at';

export function buildQueryString(params: TracksQueryParams): string {
  const sp = new URLSearchParams();
  if (params.library) sp.set('library', params.library);
  if (params.source_type?.length) {
    sp.set('source_type_in', params.source_type.join(','));
  }
  if (params.pagination.pageIndex !== 0) {
    sp.set('page', String(params.pagination.pageIndex));
  }
  sp.set('size', String(params.pagination.pageSize));
  const first = params.sorting[0];
  if (first) {
    sp.set('sort', first.id);
    sp.set('dir', first.desc ? 'desc' : 'asc');
  } else {
    sp.set('sort', DEFAULT_SORT_ID);
    sp.set('dir', 'desc');
  }

  const f = params.filters;
  if (f) {
    if (f.status_in?.length) sp.set('status_in', f.status_in.join(','));
    if (f.tier) sp.set('tier', f.tier);
    if (f.bpm_min !== undefined) sp.set('bpm_min', String(f.bpm_min));
    if (f.bpm_max !== undefined) sp.set('bpm_max', String(f.bpm_max));
    if (f.keys?.length) sp.set('keys', f.keys.join(','));
    if (f.tags_any?.length) sp.set('tags_any', f.tags_any.join(','));
    if (f.tags_all?.length) sp.set('tags_all', f.tags_all.join(','));
    if (f.untagged) sp.set('untagged', 'true');
    if (f.q) sp.set('q', f.q);
    if (f.date_from) sp.set('date_from', f.date_from);
    if (f.date_to) sp.set('date_to', f.date_to);
  }

  return sp.toString();
}

export function useTracksQuery(params: TracksQueryParams, enabled = true) {
  return useQuery<TracksListResponse>({
    queryKey: ['tracks', params],
    queryFn: () =>
      apiClient<TracksListResponse>(`/admin/tracks?${buildQueryString(params)}`),
    staleTime: STALE_TIME.list,
    placeholderData: (prev) => prev,
    enabled,
  });
}

/**
 * `GET /admin/tracks` typed with the real wire shape (`TrackListItem`), which
 * carries `bpm` / `camelot_key` / `selector_id` that the moderation queue
 * renders. Structurally identical to `TracksListResponse` above — both hit the
 * same endpoint; this alias documents the moderation-queue call site.
 */
export interface TrackListItemsResponse {
  items: TrackListItem[];
  total: number;
  cursor: string | null;
}

/** Tracks pending moderation: selector-uploaded, awaiting review. */
export const MODERATION_SOURCE_TYPES = ['selector_curated'] as const;
export const MODERATION_STATUSES: TrackStatus[] = [
  'ready_for_review',
  'requires_dup_review',
];

export interface ModerationQueueParams {
  pagination: PaginationState;
  sorting: SortingState[];
  /**
   * Optional narrowing filters layered ON TOP of the fixed moderation base
   * (source_type=selector_curated + status_in pending-review). `library` scopes
   * to one library (omit = both); `q` is a debounced free-text search.
   */
  library?: LibraryType;
  q?: string;
}

/**
 * Pure builder for the moderation-queue query. The fixed base — `source_type`
 * pinned to `selector_curated` and `status_in` to the two pending-review states
 * — is always applied; the optional `library` / `q` narrowing filters from
 * {@link ModerationQueueParams} layer on top without ever loosening the base.
 * Leaving `library` unset queries BOTH libraries. Exported so the wiring is
 * unit-testable without a React/query-client harness.
 */
export function buildModerationQueryParams(
  params: ModerationQueueParams,
): TracksQueryParams {
  return {
    pagination: params.pagination,
    sorting: params.sorting,
    library: params.library,
    source_type: [...MODERATION_SOURCE_TYPES],
    filters: {
      status_in: MODERATION_STATUSES,
      ...(params.q ? { q: params.q } : {}),
    },
  };
}

/**
 * Sidebar badge count for the moderation queue. Hits the same endpoint with a
 * minimal page so the response `total` reflects the queue size without pulling a
 * full page of rows. Shares the `['moderation-queue', ...]` key namespace so a
 * resolve-driven invalidation refreshes both the page and the badge.
 */
export function useModerationQueueCount(enabled: boolean) {
  const queryParams = buildModerationQueryParams({
    pagination: { pageIndex: 0, pageSize: 1 },
    sorting: [],
  });
  return useQuery<TrackListItemsResponse, unknown, number>({
    queryKey: ['moderation-queue', 'count'],
    queryFn: () =>
      apiClient<TrackListItemsResponse>(
        `/admin/tracks?${buildQueryString(queryParams)}`,
      ),
    select: (data) => data.total,
    staleTime: STALE_TIME.list,
    enabled,
  });
}

/**
 * The queue's pages, apart from the badge's count. The badge caches a page of
 * one `TrackListItem` too, which the row's editors leave alone: no edit in the
 * row changes the count (STORY-102).
 */
export const MODERATION_QUEUE_LIST_KEY = ['moderation-queue', 'list'] as const;

/**
 * Selector-track moderation queue. Pins the catalog query to selector-curated
 * tracks awaiting review (`ready_for_review` / `requires_dup_review`) across
 * BOTH libraries: a selector uploads to the library they choose (STORY-103),
 * and the queue is driven by provenance, not library.
 */
export function useModerationQueueQuery(params: ModerationQueueParams) {
  const queryParams = buildModerationQueryParams(params);
  return useQuery<TrackListItemsResponse>({
    queryKey: [...MODERATION_QUEUE_LIST_KEY, params],
    queryFn: () =>
      apiClient<TrackListItemsResponse>(
        `/admin/tracks?${buildQueryString(queryParams)}`,
      ),
    staleTime: STALE_TIME.list,
    placeholderData: (prev) => prev,
  });
}
